import PaginationComponent from 'Components/Common/Pagination/Pagination';
import SpinnerComponent from 'Components/Common/SpinnerComponent';
import TableCommon from 'Components/Common/Table/TableCommon';
import { IDataCard } from 'Components/Page/NFTList/ntflist';
import { PAGE_SIZE } from 'Constant';
import { DATE_FORMAT_2 } from 'Constant/Date';
import { TitleTypes } from 'Constant/Title';
import { NFTSApi } from 'Datasource/NftsApi';
import dayjs from 'dayjs';
import useMessage from 'Hooks/useMessage';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Cell } from 'react-table';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

const handleAddIndex = (cards: IDataCard[], pageIndex: number) => {
  cards = cards.map((card, key) => {
    const index = (pageIndex - 1) * PAGE_SIZE + 1 + key;
    return { ...card, index };
  });
  return cards;
};

export default function TableList(): JSX.Element {
  const columns = useMemo(
    () => [
      {
        Header: '',
        accessor: 'empty',
        className: 'pe-none',
        columns: [
          {
            Header: 'No.',
            accessor: 'index',
            maxWidth: 100,
            width: 100,
          },
          {
            Header: '商品ID',
            accessor: 'cardCD',
            width: 360,
            Cell: ({ row }: Cell<IDataCard>) => {
              return (
                <div className="text-truncate text-start" title={row.original.cardCD}>
                  {row.original.cardCD}
                </div>
              );
            },
          },
          {
            Header: 'タイトル',
            accessor: 'title',
            maxWidth: 400,
            width: 350,
            Cell: ({ row }: Cell<IDataCard>) => {
              return (
                <div className="text-truncate text-start" title={row.original.title}>
                  {row.original.title}
                </div>
              );
            },
          },
          {
            Header: 'アーティスト',
            accessor: 'artistName',
            maxWidth: 200,
            width: 180,
            Cell: ({ row }: Cell<IDataCard>) => {
              return (
                <div className="text-truncate text-start" title={row.original.artistName}>
                  {row.original.artistName}
                </div>
              );
            },
          },
          {
            Header: 'タイプ',
            accessor: 'type',
            maxWidth: 150,
            width: 130,
            Cell: ({ row }: Cell<IDataCard>) => {
              return (
                <div className="text-truncate text-start">
                  {TitleTypes.find((type) => row.original.type === type.value)?.label}
                </div>
              );
            },
          },
          {
            Header: '曲数',
            accessor: 'songQuantity',
            maxWidth: 150,
            width: 100,
          },
          {
            Header: '特典名称',
            accessor: 'digitalTokuten',
            maxWidth: 400,
            width: 350,
            Cell: ({ row }: Cell<IDataCard>) => {
              return (
                <Link
                  title={row.original.digitalTokutenName}
                  to={`/digital-tokuten/${row.original.digitalTokutenId}`}
                  className={`${
                    !row.original.digitalTokutenId && 'text-decoration-none text-black pe-none'
                  }`}
                >
                  <span className="d-block text-truncate text-start">
                    {row.original.digitalTokutenName}
                  </span>
                </Link>
              );
            },
          },
          {
            Header: '形態',
            accessor: 'digitalType',
            maxWidth: 200,
            width: 150,
          },
          {
            Header: '販売価格',
            accessor: 'price',
            maxWidth: 200,
            width: 150,
            Cell: ({ row }: Cell<IDataCard>) => {
              return (
                <div className="text-end">
                  {row.original.price && Number(row.original.price)?.toLocaleString()}
                </div>
              );
            },
          },
          {
            Header: '数量限定',
            accessor: 'limitedQuantity',
            maxWidth: 200,
            width: 100,
            Cell: () => {
              return <div className="text-truncate" />;
            },
          },
          {
            Header: '特典販売',
            accessor: 'saleTokenDate',
            maxWidth: 300,
            width: 200,
            Cell: ({ row }: Cell<IDataCard>) => {
              return row.original.saleStartDate
                ? dayjs(row.original.saleStartDate).format(DATE_FORMAT_2)
                : '';
            },
          },
          {
            Header: '発売⽇',
            accessor: 'downloadDate',
            maxWidth: 300,
            width: 200,
            Cell: ({ row }: Cell<IDataCard>) => {
              return row.original.downloadDate
                ? dayjs(row.original.downloadDate).format(DATE_FORMAT_2)
                : '';
            },
          },
          {
            Header: '登録⽇',
            accessor: 'createdAt',
            maxWidth: 300,
            width: 200,
            Cell: ({ row }: Cell<IDataCard>) => {
              return row.original.createdAt
                ? dayjs(row.original.createdAt).format(DATE_FORMAT_2)
                : '';
            },
          },
          {
            Header: 'JANコード',
            accessor: 'janCode',
            maxWidth: 300,
            width: 200,
            Cell: ({ row }: Cell<IDataCard>) => {
              return row.original.janCode;
            },
          },
        ],
      },
    ],
    [],
  );

  const { openMessageError } = useMessage();

  const [query] = useQueryParams({
    page: NumberParam,
    queryArtist: StringParam,
    artistId: StringParam,
    artistIds: StringParam,
    type: StringParam,
    downloadDateStart: StringParam,
    downloadDateEnd: StringParam,
  });
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState<IDataCard[]>([]);
  const [total, setTotal] = useState(0);

  const handleGetCards = async (ids: any) => {
    try {
      setLoading(true);
      const {
        data: { data },
      } = await NFTSApi.getAllCard({
        page: query.page ?? 1,
        limit: PAGE_SIZE,
        artistId: ids.artistIds,
        downloadDateStart: ids.downloadDateStart,
        downloadDateEnd: ids.downloadDateEnd,
      });
      const cards = handleAddIndex(data.cards, query.page ?? 1);

      setCards(cards);
      setTotal(data.totalCount);
    } catch (error) {
      openMessageError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const downloadDateStart = query.downloadDateStart
      ? dayjs(query.downloadDateStart).toISOString()
      : null;

    const downloadDateEnd = query.downloadDateEnd
      ? dayjs(query.downloadDateEnd).endOf('d').toISOString()
      : null;

    const ids = {
      artistIds: query.artistIds ?? '',
      downloadDateStart: downloadDateStart,
      downloadDateEnd: downloadDateEnd,
    };
    handleGetCards(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.artistIds, query.downloadDateStart, query.downloadDateEnd]);

  return (
    <>
      <SpinnerComponent isLoading={loading} />
      <div className="mt-2 container-fluid">
        <Row>
          <Col md={12} className="d-flex justify-content-end p-0 px-4">
            <PaginationComponent activePage={query.page ?? 1} total={total} perPage={PAGE_SIZE} />
          </Col>
        </Row>
      </div>
      <div className="table-nft-container">
        {!isEmpty(cards) ? (
          <>
            <Row className="mt-2">
              <Col md={12} className="d-flex justify-content-center p-0">
                <TableCommon heightBody="calc(100vh - 240px)" data={cards} columns={columns} />
              </Col>
            </Row>
          </>
        ) : (
          <div className="vh-100-final d-flex align-items-center justify-content-center">
            商品が登録されていません
          </div>
        )}
      </div>
    </>
  );
}
